import { createContext, FC, ReactNode, useContext, useMemo } from 'react';
import {
  useGetCurrentUserQuery,
  useGetDegreeQuery,
  useGetDegreeCoursesQuery,
  CourseWithProgress,
} from '@teachur/http-client';
import type { Degree } from '@teachur/http-client';
import { CourseStatus } from '../types';

export type CourseStatusMap = Record<CourseStatus, CourseWithProgress[]> & {
  all: CourseWithProgress[];
};

export interface Credits {
  internal: number;
  external: number;
}

export interface DegreePageContextValue {
  // Data
  degreeId: number;
  degree: Degree | undefined;
  courses: CourseStatusMap;
  credits: Credits;

  // Loading
  isLoading: boolean;
}

export const DegreePageContext = createContext<DegreePageContextValue | undefined>(undefined);

interface DegreePageProviderProps {
  degreeId: number;
  children: ReactNode;
}

export const DegreePageProvider: FC<DegreePageProviderProps> = ({ degreeId, children }) => {
  const { data: degree, isLoading: isDegreeLoading } = useGetDegreeQuery(degreeId);
  const { data: currentUser, isLoading: isCurrentUserLoading } = useGetCurrentUserQuery();

  // const isEnrolled = currentUser?.enrolledDegrees?.some((d: any) => d.degreeId === degreeId) ?? false;

  const { data: curriculumCourses = [], isLoading: isCurriculumLoading } =
    useGetDegreeCoursesQuery(degreeId);
  // const { data: userCoursesData, isLoading: isUserCoursesLoading } = useGetCoursesQuery({ page: 1, pageSize: 50 }, { enabled: isEnrolled });
  // const userCourses = userCoursesData?.data as CourseWithProgress[] || [];
  const courses = useMemo(() => {
    // const coursesToUse = isEnrolled ? userCourses : curriculumCourses;
    const coursesToUse = curriculumCourses;

    return coursesToUse.reduce<CourseStatusMap>(
      (acc, course) => {
        const status = course.status as keyof CourseStatusMap;
        if (status && acc[status]) {
          acc[status].push(course);
        }
        return acc;
      },
      { all: coursesToUse, completed: [], inProgress: [], notStarted: [] }
    );
  }, [curriculumCourses]);

  const isLoading =
    isDegreeLoading ||
    // (isEnrolled ? isUserCoursesLoading : false) ||
    isCurriculumLoading ||
    isCurrentUserLoading;
  const credits = useMemo(() => {
    return (currentUser?.credits ?? []).reduce(
      (acc: Credits, credit) => {
        if (credit.isExternal) {
          acc.external += credit.amount;
        } else {
          acc.internal += credit.amount;
        }
        return acc;
      },
      { internal: 0, external: 0 }
    );
  }, [currentUser]);

  const value: DegreePageContextValue = {
    degreeId,
    degree,
    courses,
    credits,
    isLoading,
  };

  return <DegreePageContext.Provider value={value}>{children}</DegreePageContext.Provider>;
};

export const useDegreePageContext = (): DegreePageContextValue => {
  const context = useContext(DegreePageContext);

  if (!context) {
    throw new Error('useDegreePageContext must be used within DegreePageProvider');
  }

  return context;
};
